import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Ruler, Calendar, ShieldCheck, Plus, X } from "lucide-react";

const HERO_IMG = "/images/hero-house.jpg";
const WORKER_IMG = "/images/worker.jpg";
const GRASS_IMG = "/images/grass-bright.jpg";

const PRICE_PER_SQM = 0.5;
const PRICE_ROUND_TO = 5;
const MIN_QUOTE_AUD = 30;

const ceilArea = (n) => Math.ceil(Number(n) || 0);
const roundUpToFive = (n) => (n > 0 ? Math.ceil(n / PRICE_ROUND_TO) * PRICE_ROUND_TO : 0);

export default function Landing() {
  const [areas, setAreas] = useState([{ length: 10, width: 6 }]);

  const rawArea = areas.reduce((s, a) => s + (Number(a.length) || 0) * (Number(a.width) || 0), 0);
  const totalArea = ceilArea(rawArea);
  const rawPrice = totalArea * PRICE_PER_SQM;
  const roundedPrice = roundUpToFive(rawPrice);
  const minimumApplied = totalArea > 0 && roundedPrice < MIN_QUOTE_AUD;
  const totalPrice = (minimumApplied ? MIN_QUOTE_AUD : roundedPrice).toFixed(2);

  const setArea = (idx, key, val) => {
    const next = [...areas];
    next[idx] = { ...next[idx], [key]: val };
    setAreas(next);
  };
  const roundDimensionOnBlur = (idx, key, val) => {
    const n = Math.ceil(Number(val) || 0);
    setArea(idx, key, n);
  };
  const addArea = () => setAreas([...areas, { length: 0, width: 0 }]);
  const removeArea = (idx) => setAreas(areas.filter((_, i) => i !== idx));

  return (
    <div className="bg-warm-sand">
      {/* HERO */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-24" data-testid="landing-hero">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <div className="label-overline mb-6">— Mow the modern way</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tighter text-deep-forest">
              Mowing<br/>
              <span className="italic font-light">your street.</span>
            </h1>
            <p className="mt-8 max-w-xl text-deep-forest/70 text-lg leading-relaxed">
              Tell us the size of your lawn — width by length. Get an <span className="text-terracotta font-medium">instant transparent quote</span> in seconds. No surprises, no door-step estimates.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/book" data-testid="hero-cta-book" className="btn-primary">
                Get an instant quote <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#how" className="btn-secondary">How it works</a>
            </div>

            <div className="mt-14 grid grid-cols-3 max-w-md gap-6">
              <div>
                <div className="font-serif text-3xl text-deep-forest">60s</div>
                <div className="text-xs uppercase tracking-widest text-deep-forest/60 mt-1">instant quote</div>
              </div>
              <div>
                <div className="font-serif text-3xl text-deep-forest">2–4d</div>
                <div className="text-xs uppercase tracking-widest text-deep-forest/60 mt-1">turnaround</div>
              </div>
              <div>
                <div className="font-serif text-3xl text-terracotta">10%</div>
                <div className="text-xs uppercase tracking-widest text-deep-forest/60 mt-1">off per neighbour</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <img src={HERO_IMG} alt="Lawn" className="w-full h-[460px] object-cover" />
            </div>
            <div className="mt-6 bg-bone-white border border-deep-forest/10 p-6 max-w-[260px] hidden sm:block">
              <div className="label-overline">Today</div>
              <div className="font-serif text-2xl text-deep-forest mt-2 leading-tight">12 lawns mowed in your neighbourhood.</div>
            </div>
          </div>
        </div>
      </section>

      <div className="divider-thin max-w-7xl mx-auto" />

      {/* HOW TO MEASURE */}
      <section className="bg-bone-white" id="measure" data-testid="measure-guide">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <div className="label-overline mb-4">— How to measure</div>
              <h2 className="font-serif text-4xl lg:text-5xl tracking-tight text-deep-forest leading-tight">Two numbers per patch.<br/><span className="italic font-light text-[#2F6840]">That&apos;s it.</span></h2>
              <p className="mt-6 text-deep-forest/70 leading-relaxed">
                For each lawn area, <strong className="text-deep-forest">measure or walk</strong> the <strong className="text-deep-forest">longest length</strong> and the <strong className="text-deep-forest">widest width</strong> in metres to get an estimate — like the photo. Add every separate patch: front strip, back yard, that awkward bit by the fence.
              </p>
              <ul className="mt-8 space-y-4 text-sm text-deep-forest/80">
                <li className="flex gap-3">
                  <span className="text-terracotta font-serif text-xl leading-none">01</span>
                  <span>Measure or walk the longest side.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-terracotta font-serif text-xl leading-none">02</span>
                  <span>Measure or walk the widest side. Don&apos;t worry about odd shapes — just take the widest point.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-terracotta font-serif text-xl leading-none">03</span>
                  <span>If you have multiple separate patches, click <em>+ Add area</em> and enter each one on its own row.</span>
                </li>
              </ul>
              <p className="mt-6 text-xs text-deep-forest/60 leading-relaxed" data-testid="measure-onsite-note">
                Only estimating? Not a problem. All new sites are measured on the first visit and any discrepancies in cost are adjusted before your next mow.
              </p>
              <a href="#quote" data-testid="measure-jump-to-quote" className="btn-primary mt-10 inline-flex">Get your quote <ArrowRight className="w-4 h-4" /></a>
            </div>
            <div className="lg:col-span-7">
              <figure className="border border-deep-forest/10 bg-warm-sand p-3 lg:p-4 shadow-sm">
                <img
                  src="/images/measure-example.jpg"
                  alt="Aerial view of a house showing two lawn areas measured — Area 1 at 10.80m × 2.60m and Area 2 at 3.50m × 6.00m"
                  className="w-full h-auto block"
                  width="2476"
                  height="1382"
                  data-testid="measure-example-image"
                  loading="eager"
                  decoding="sync"
                />
              </figure>
            </div>
          </div>
        </div>
      </section>

      <div className="divider-thin max-w-7xl mx-auto" />

      {/* CALCULATOR */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24" id="quote" data-testid="landing-calculator">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="label-overline mb-4">— The Quote</div>
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tight text-deep-forest leading-tight">Pay for the grass<br/>you actually have.</h2>
            <p className="mt-6 text-deep-forest/70 leading-relaxed">
              Add every patch — front, back, sides, that strip near the driveway. We tally the square meters and hand you a clean, instant total. That&apos;s it.
            </p>
            <Link to="/register" data-testid="calc-cta-signup" className="btn-primary mt-8">Create an account to book <ArrowRight className="w-4 h-4" /></Link>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-bone-white border border-deep-forest/10 p-8">
              <div className="flex items-center justify-between">
                <div className="label-overline">Lawn areas</div>
                <button data-testid="calc-add-area-btn" onClick={addArea} className="text-sm flex items-center gap-2 px-4 py-2 border-2 border-[#2F6840] bg-[#2F6840]/10 text-[#2F6840] hover:bg-[#2F6840] hover:text-warm-sand transition-colors font-semibold">
                  <Plus className="w-4 h-4" /> Add area
                </button>
              </div>

              <p className="text-xs text-deep-forest/55 mt-3 italic" data-testid="calc-measure-tip">
                💡 Tip: measure or walk the longest length and widest width of each lawn area to get an estimate.
              </p>

              <div className="mt-6 space-y-5">
                {areas.map((a, i) => (
                  <div key={i} className="grid grid-cols-12 gap-4 items-end" data-testid={`calc-area-row-${i}`}>
                    <div className="col-span-5">
                      <label className="text-xs uppercase tracking-widest text-deep-forest/60">Length (m)</label>
                      <input
                        data-testid={`calc-length-${i}`}
                        type="number" min="0" step="1"
                        value={a.length}
                        onChange={(e) => setArea(i, "length", e.target.value)}
                        onBlur={(e) => roundDimensionOnBlur(i, "length", e.target.value)}
                        className="field-line" />
                    </div>
                    <div className="col-span-1 text-center text-deep-forest/40 pb-3">×</div>
                    <div className="col-span-5">
                      <label className="text-xs uppercase tracking-widest text-deep-forest/60">Width (m)</label>
                      <input
                        data-testid={`calc-width-${i}`}
                        type="number" min="0" step="1"
                        value={a.width}
                        onChange={(e) => setArea(i, "width", e.target.value)}
                        onBlur={(e) => roundDimensionOnBlur(i, "width", e.target.value)}
                        className="field-line" />
                    </div>
                    <div className="col-span-1 flex justify-end">
                      {areas.length > 1 && (
                        <button data-testid={`calc-remove-${i}`} onClick={() => removeArea(i)} className="p-2 text-deep-forest/40 hover:text-terracotta">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="divider-thin my-8" />

              <div className="flex items-end justify-between">
                <div>
                  <div className="label-overline">Total area</div>
                  <div className="font-serif text-3xl text-deep-forest mt-1" data-testid="calc-total-area">{totalArea} m²</div>
                </div>
                <div className="text-right">
                  <div className="label-overline">Your price</div>
                  <div className="font-serif text-5xl text-terracotta mt-1 leading-none" data-testid="calc-total-price">${totalPrice}<span className="text-sm text-deep-forest/50 align-top ml-1 font-sans">AUD</span></div>
                </div>
              </div>
              <p className="text-xs text-deep-forest/55 mt-4 italic" data-testid="calc-rounding-note">
                * Lengths are rounded up to the next whole metre for edging, and the final price is rounded up to the nearest $5.
              </p>
              {minimumApplied && (
                <div
                  data-testid="calc-minimum-notice"
                  className="mt-4 flex gap-3 bg-status-pending-bg/60 border-l-2 border-terracotta px-4 py-3"
                >
                  <span className="font-serif text-terracotta text-lg leading-none mt-0.5">$30</span>
                  <p className="text-xs text-deep-forest/80 leading-relaxed">
                    A <strong>$30 minimum charge</strong> has been applied. Your lawn works out to <strong>${roundedPrice.toFixed(2)}</strong>, but we can&apos;t send a crew out for less than $30 — cost of fuel, kit and travel.
                  </p>
                </div>
              )}
              <div
                data-testid="calc-verify-onsite-notice"
                className="mt-3 text-xs text-deep-forest/60 leading-relaxed"
              >
                <strong className="text-deep-forest/80">Fair pricing promise.</strong> All new sites are measured on the first visit — if there&apos;s a discrepancy, we&apos;ll adjust the cost (up or down) before the next mow.
              </div>

              <Link
                to="/book"
                data-testid="calc-book-now-btn"
                className="btn-primary w-full justify-center mt-6 text-base py-4"
              >
                Book this mow — ${totalPrice} AUD <ArrowRight className="w-4 h-4" />
              </Link>
              <p className="text-xs text-deep-forest/50 mt-3 text-center">
                Recurring every 3 weeks · pause or cancel anytime
              </p>
            </div>

            <div className="mt-6 bg-deep-forest text-warm-sand p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between" data-testid="tailored-quote-card">
              <div>
                <div className="label-overline text-warm-sand/70 mb-1">— Something bigger?</div>
                <div className="font-serif text-xl leading-snug">Tailored quote, NDIS, or Owners Corporation?</div>
                <div className="text-warm-sand/70 text-sm mt-1">Get in touch and we&apos;ll come back to you with a custom offer.</div>
              </div>
              <a href="mailto:info@streetmow.com.au" data-testid="tailored-quote-email" className="bg-terracotta text-warm-sand px-5 py-3 hover:bg-[#B34A2E] transition-colors whitespace-nowrap font-medium">
                info@streetmow.com.au
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="bg-moss/40 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="label-overline mb-4">— How it works</div>
              <h2 className="font-serif text-4xl lg:text-5xl tracking-tight text-deep-forest leading-tight">Three steps to a sharper lawn.</h2>
              <img src="/logos/logo_streetmow_tight.png" alt="StreetMow logo" className="mt-10 w-full h-[420px] object-contain" />
            </div>
            <div className="lg:col-span-7 space-y-10">
              {[
                { i: Ruler, n: "01", t: "Measure", d: "Add one or more lawn areas. We compute the square meters." },
                { i: Calendar, n: "02", t: "Schedule", d: "Pick a preferred date. We confirm within a day." },
                { i: ShieldCheck, n: "03", t: "Mow", d: "Our crew arrives, mows, and leaves it cleaner than they found it." },
              ].map(({ i: Icon, n, t, d }, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-6 items-start border-t border-deep-forest/10 pt-8">
                  <div className="col-span-2 font-serif text-4xl text-terracotta">{n}</div>
                  <div className="col-span-10">
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5 text-deep-forest" />
                      <h3 className="font-serif text-2xl text-deep-forest">{t}</h3>
                    </div>
                    <p className="mt-3 text-deep-forest/70 leading-relaxed">{d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden">
        <img src={GRASS_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-12 py-32 text-center">
          <h2 className="font-serif text-5xl lg:text-6xl text-warm-sand leading-tight italic font-light drop-shadow-lg">Relax and let us do the <span className="not-italic">hard work</span> for you.</h2>
          <div className="mt-10 flex justify-center gap-4 flex-wrap">
            <Link to="/register" data-testid="cta-register-btn" className="btn-primary">Create an account</Link>
            <Link to="/book" data-testid="cta-quote-btn" className="bg-warm-sand text-deep-forest px-6 py-3 hover:bg-bone-white transition-colors font-medium">Just get a quote</Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-deep-forest/10 py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex justify-between text-sm text-deep-forest/60">
          <span>© StreetMow — neighbourhood mowing</span>
          <span>Instant quote, honest work.</span>
        </div>
      </footer>
    </div>
  );
}
